import * as Sentry from '@sentry/react';
import isAutoBillingAddon from './is-auto-billing-addon.ts';

/**
 * @description Get the subscription status based on the subscription and the latest invoice.
 * @param {import('../types').Subscription} subscription
 * @param {import('stripe').Stripe.Invoice} latestInvoice
 * @param {boolean} [verbose]
 * @returns {import('../types').SubscriptionStatus}
 */
export default function getSubscriptionStatus(subscription, latestInvoice, verbose = false) {
    if (!subscription) {
        return {};
    }

    if (!subscription || !subscription?.status || !latestInvoice || !latestInvoice?.status) {
        return {};
    }

    /** @type {{status: import('../types').StripeSubscriptionStatus}} */
    const { status } = subscription;

    /** @type {boolean} */
    const _hasAutoPriceEnabled = !!subscription?.meta?.addons?.some(isAutoBillingAddon);
    /** @type {boolean} */
    const _isTrial = status === 'trialing';
    /** @type {boolean} */
    const _isActive = status === 'active';
    /** @type {boolean} */
    const _isIncomplete = status === 'incomplete';
    /** @type {boolean} */
    const _isCancelled = status === 'canceled';
    /** @type {boolean} */
    const _isGrace = status === 'past_due' || status === 'unpaid';
    /** @type {boolean} */
    const _isInactive = _isCancelled || (!_isTrial && !_isActive && !_isGrace && !_isIncomplete);
    /** @type {boolean} */
    let _isPending = false;
    /** @type {boolean} */
    const requiresAction =
        latestInvoice?.payment_intent?.status === 'requires_action' ||
        latestInvoice?.payment_intent?.status === 'requires_payment_method';
    const invoiceStatus = latestInvoice?.status;
    /** @type {boolean} */
    const invoiceRequiresAction = invoiceStatus === 'open' && requiresAction;
    /** @type {boolean} */
    const invoiceDueToSubscriptionCycle = latestInvoice?.billing_reason === 'subscription_cycle';

    // When the last invoice has the status 'open' it's a pending update and the user should have the option
    // of finalising it. If the pending update doesn't take effect, e. g. it expired after 24 hours, or the
    // user subscribed to a different plan, the invoice will be marked as void.
    if (
        (status === 'active' ||
            (_hasAutoPriceEnabled && status === 'past_due' && !invoiceDueToSubscriptionCycle)) &&
        invoiceRequiresAction
    ) {
        if (verbose) {
            if (_hasAutoPriceEnabled) {
                console.log(
                    '[API] Subscription status, invoice open from auto billing:',
                    latestInvoice?.payment_intent?.status,
                );
            } else {
                console.log(
                    '[API] Subscription status, invoice open:',
                    latestInvoice?.payment_intent?.status,
                );
            }
        }

        _isPending = true;
    }

    const isGrace = _isGrace && (!_hasAutoPriceEnabled || invoiceDueToSubscriptionCycle);
    const isPending = _isPending && !_hasAutoPriceEnabled;

    Sentry.setTags({
        trial: _isTrial || false,
        dunning: isGrace || false,
        autobilling: _hasAutoPriceEnabled || false,
        incomplete_or_pending: isPending ? 'pending' : _isIncomplete ? 'incomplete' : 'no',
    });

    return {
        isTrial: _isTrial,
        isActive: _isActive,
        isIncomplete: _isIncomplete,
        isInactive: _isInactive,
        isGrace,
        isPending,
        isAutoBillingGrace: _hasAutoPriceEnabled && _isPending,
        hasAutoBillingEnabled: _hasAutoPriceEnabled,
    };
}
